// src/hooks/useConfirmDialog.js
import { useState, useCallback } from 'react';
import ConfirmDialog from '../components/ui/ConfirmDialog';

/**
 * Custom hook to handle confirmation dialogs.
 * Returns a tuple with the ConfirmDialog component and a confirm function.
 * 
 * @returns {[React.Component, Function]} A tuple containing the ConfirmDialog component and a confirm function
 */
const useConfirmDialog = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    title: 'Confirm',
    content: 'Are you sure?',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    isDelete: false
  });
  const [resolveRef, setResolveRef] = useState(null);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
    if (resolveRef) {
      resolveRef(false);
      setResolveRef(null);
    }
  }, [resolveRef]);

  const handleConfirm = useCallback(() => {
    setDialogOpen(false);
    if (resolveRef) {
      resolveRef(true);
      setResolveRef(null);
    }
  }, [resolveRef]);

  const confirm = useCallback((config = {}) => {
    return new Promise(resolve => {
      setDialogConfig({ ...dialogConfig, ...config });
      setResolveRef(() => resolve);
      setDialogOpen(true);
    });
  }, [dialogConfig]);

  const ConfirmDialogComponent = useCallback(() => {
    return (
      <ConfirmDialog
        open={dialogOpen}
        title={dialogConfig.title}
        content={dialogConfig.content}
        confirmText={dialogConfig.confirmText}
        cancelText={dialogConfig.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleClose}
        isDelete={dialogConfig.isDelete}
      />
    );
  }, [dialogOpen, dialogConfig, handleConfirm, handleClose]);

  return [ConfirmDialogComponent, confirm];
};

export default useConfirmDialog;